import { useEffect } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { useCatalogUnresolvableRedirect } from 'app/hooks/useSubscriptionPlans/useCatalogUnresolvableRedirect';

import { trackComparePlansModalViewed } from '../trackPlanAndPaymentEvents';
import { useHandleSelectNewTierPlan } from '../useHandleSelectNewTierPlan';
import { useComparePlansTable } from './useComparePlansTable';
import type { ComparePlansModalProps } from './types';
import * as S from './styles';

const TITLE = 'Comparar todos os planos';
const DESCRIPTION =
  'Limites mensais de cada ferramenta. Você pode trocar de plano a qualquer momento.';
const CATALOG_ERROR_TITLE = 'Não foi possível carregar os planos';
const CATALOG_ERROR_MESSAGE = 'Tente novamente em instantes ou feche e volte mais tarde.';

export function ComparePlansModal({
  isOpen,
  onClose,
  onBackToExplore,
  formik,
  selectPlan,
  user,
  subscriptionStatus,
  url,
}: ComparePlansModalProps) {
  const { isLoading, columns, rows, error, refetch, offers } = useComparePlansTable();

  useCatalogUnresolvableRedirect({ error, isOpen, onClose });
  const isUnresolvable = error === 'SUBSCRIPTION_STATUS_UNRESOLVABLE';

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    trackComparePlansModalViewed();
  }, [isOpen]);

  const handleSelectPlan = useHandleSelectNewTierPlan({
    formik,
    selectPlan,
    user,
    subscriptionStatus,
    url,
    offers,
  });

  if (isUnresolvable) {
    return null;
  }

  return (
    <S.StyledDialog open={isOpen} onClose={onClose}>
      <S.Head>
        <S.TitleGroup>
          <S.HeaderIconButton onClick={onBackToExplore} aria-label="Voltar">
            <ChevronLeftIcon />
          </S.HeaderIconButton>
          <div>
            <S.Title>{TITLE}</S.Title>
            <S.Description>{DESCRIPTION}</S.Description>
          </div>
        </S.TitleGroup>
        <S.HeaderIconButton onClick={onClose} aria-label="Fechar">
          <CloseIcon />
        </S.HeaderIconButton>
      </S.Head>

      <S.Body>
        {isLoading ? (
          <S.LoadingState>
            <CircularProgress size={32} />
          </S.LoadingState>
        ) : error === 'PRICING_CATALOG_INCOMPLETE' ? (
          <S.ResultState>
            <S.ResultIcon $variant="error">
              <ErrorOutlineIcon />
            </S.ResultIcon>
            <S.ResultTitle>{CATALOG_ERROR_TITLE}</S.ResultTitle>
            <S.ResultMessage>{CATALOG_ERROR_MESSAGE}</S.ResultMessage>
          </S.ResultState>
        ) : (
          <S.TableScrollContainer>
            <S.Table>
              <thead>
                <tr>
                  <th scope="col" />
                  {columns.map((column) => {
                    const isHighlighted = column.key === 'ultimate_2026';

                    return (
                      <S.PlanHeaderCell key={column.key} scope="col" $highlighted={isHighlighted}>
                        <S.PlanName>{column.title}</S.PlanName>
                        <S.PlanPrice>{column.price}/mês</S.PlanPrice>
                        {column.pill && (
                          <S.Pill $variant={column.pill}>
                            {column.pill === 'current' ? 'Plano atual' : 'Popular'}
                          </S.Pill>
                        )}
                      </S.PlanHeaderCell>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.attribute}>
                    <th scope="row">{row.attribute}</th>
                    {columns.map((column) => (
                      <S.Cell
                        key={column.key}
                        $highlighted={column.key === 'ultimate_2026'}
                        $muted={row.values[column.key] === '—'}
                      >
                        {row.values[column.key]}
                      </S.Cell>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td />
                  {columns.map((column) => {
                    const isCurrentTier = column.isCurrent;

                    if (isCurrentTier) {
                      return (
                        <S.Cell key={column.key} $highlighted={column.key === 'ultimate_2026'}>
                          <S.DisabledCtaButton type="button" disabled>
                            Plano atual
                          </S.DisabledCtaButton>
                        </S.Cell>
                      );
                    }

                    if (!column.isUpgradeable) {
                      return (
                        <S.Cell key={column.key} $highlighted={column.key === 'ultimate_2026'}>
                          <S.DisabledCtaButton type="button" disabled>
                            Assinar {column.title}
                          </S.DisabledCtaButton>
                        </S.Cell>
                      );
                    }

                    return (
                      <S.Cell key={column.key} $highlighted={column.key === 'ultimate_2026'}>
                        <S.CtaButton
                          type="button"
                          $primary={column.key === 'ultimate_2026'}
                          onClick={() => handleSelectPlan(column.key)}
                        >
                          Assinar {column.title}
                        </S.CtaButton>
                      </S.Cell>
                    );
                  })}
                </tr>
              </tbody>
            </S.Table>
          </S.TableScrollContainer>
        )}
      </S.Body>

      <S.Foot $sideBySide>
        {error === 'PRICING_CATALOG_INCOMPLETE' ? (
          <>
            <S.SecondaryButton onClick={onClose}>Fechar</S.SecondaryButton>
            <S.PrimaryButton type="button" onClick={refetch}>
              Tentar novamente
            </S.PrimaryButton>
          </>
        ) : (
          <>
            <S.SecondaryButton onClick={onBackToExplore}>Voltar aos planos</S.SecondaryButton>
            <S.SecondaryButton onClick={onClose}>Fechar</S.SecondaryButton>
          </>
        )}
      </S.Foot>
    </S.StyledDialog>
  );
}

export default ComparePlansModal;
